import { captureException } from '@sentry/nuxt';
import { RegisterUserRequirementsRequest } from 'gportal-grpc/gportal/api/user/v1/requests_pb';
import type { FilterSelectItemsFn } from '~/components/form/select/types';

export type BillingCountryOption = { code: string; name: string };

// The country list and the per-country address rules, as the backend states them. The single
// place that knows the shape of `RegisterUserRequirements`: registration reads it through
// `useCountries`, which binds these lists to its own form data, and the checkout address form
// reads it directly.
//
// `countriesWithPostalCode` is wider than `countriesRequiringPostalCode`: it is every country
// that has a postal code at all, which is where the field is worth offering even though we would
// not refuse the address without it (TV-1970).
//
// The returned `asyncData` is awaitable, so a caller that needs the list resolved before it
// renders can await it. The checkout form deliberately does not: awaiting makes the calling setup
// async, and an async component nested in an already-mounted page re-suspends that page's
// Suspense boundary and blanks it. Every computed below defaults until the data arrives.
export default () => {
  const $grpcUser = useGrpcUser();
  const { locale } = useI18n();

  // The response depends on the locale and nothing else, so the key is keyed on
  // the locale alone — as a getter, so switching language refetches.
  const asyncData = useAsyncData(
    () => `billing-countries-${locale.value}`,
    async () => {
      const request = new RegisterUserRequirementsRequest();
      request.locale = locale.value;

      const { response, error } = await $grpcUser.executeApiCallPrimary(
        'registerUserRequirements',
        request,
      );

      if (error == null && response != null) {
        return {
          countriesRequiringFullAddress: response.countriesRequiringFullAddress,
          countriesRequiringPostalCode: response.countriesRequiringPostalCode,
          countriesWithPostalCode: response.countriesWithPostalCode,
          countryOptionsList: response.countryOptions.map((c) => ({
            code: c.code,
            name: c.name,
          })),
        };
      }

      captureException(error);
      throw error;
    },
    { deep: true },
  );

  const { status, refresh, data } = asyncData;

  const availableCountries = computed<BillingCountryOption[]>(
    () => data.value?.countryOptionsList ?? [],
  );

  const countriesWithPostalCode = computed<string[]>(
    () => data.value?.countriesWithPostalCode ?? [],
  );

  const countriesRequiringPostalCode = computed<string[]>(
    () => data.value?.countriesRequiringPostalCode ?? [],
  );

  const countriesRequiringFullAddress = computed<string[]>(
    () => data.value?.countriesRequiringFullAddress ?? [],
  );

  const handleFilterCountries: FilterSelectItemsFn<BillingCountryOption> = (
    items,
    query,
  ) => {
    if (query == null || query.length === 0) return Promise.resolve(items);

    return Promise.resolve(
      items.filter(
        ({ code, name }) =>
          name.toLowerCase().indexOf(query.toLowerCase()) >= 0 ||
          code.toLowerCase() === query.toLowerCase(),
      ),
    );
  };

  return {
    asyncData,
    availableCountries,
    countriesWithPostalCode,
    countriesRequiringPostalCode,
    countriesRequiringFullAddress,
    countriesFetchState: status,
    fetchCountries: refresh,
    handleFilterCountries,
  };
};
